import Event from './common/event';
import { uniqueId } from './utils/util';
import { ClassTypeError } from './utils/error';
import check from './utils/check';
import {
  Box3,
  LoadingManager,
  REVISION,
  Vector3,
  TextureLoader,
  ShaderMaterial,
  MeshBasicMaterial,
  LinearEncoding,
  EdgesGeometry,
  Mesh,
  Color,
  sRGBEncoding,
  ReinhardToneMapping,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MTLLoader } from './source/MTLLoader.js';
import { vertexShader, fragmentShader } from './shader/flowLightShader';
import { vertexShader as edgeVertexShader, fragmentShader as edgeFragmentShader } from './shader/edgeFlowLightShader';

import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

import {
  MeshoptDecoder,
} from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

const { isUndefined, isArray, isNumber } = check;

let MANAGER;
const DEFAULT_XHR_PROGRESS = 0.6 + Math.random() / 5;
const THREE_PATH = `https://unpkg.com/three@0.${REVISION}.x`;
let DRACO_LOADER;
let KTX2_LOADER;

function isVector3(value) {
  return isArray(value) && value.length === 3 && value.every(v => isNumber(v));
}

export default class Model extends Event {
  constructor(opts, scene) {
    super();
    const {
      // map,
      position = [0, 0, 0],
      id = uniqueId(),
      url = 'https://mapapi.qq.com/web/dataLayerTest/model/TengXunDaSha.gltf',
      materialMixUrl,
      targetUrl,
      // zIndex = 0,
      // anchor = [0, 0, 0],
      scale = [1, 1, 1],
      rotation = [0, 0, 0],
      mtlUrl = '',
    } = opts;
    Object.assign(this, {
      id,
      url,
      mtlUrl,
      materialMixUrl,
      targetUrl,
    });
    this.clips = [];
    this.firstProgress = DEFAULT_XHR_PROGRESS;
    this.show();
    this.scene = scene;
    this.initLoadingManager();
    const initModelData = () => {
      this.setRotation(rotation);
      this.setScale(scale);
      this.setPosition(position);
    };
    // 使用callback方式 避免loaded事件早于单个模型load事件
    this.load(initModelData);
  }
  load(callback) {
    const fileType = this.url.substring(this.url.lastIndexOf('.') + 1);
    if (!this.url || !fileType) return;
    if (!this.loading) {
      switch (fileType) {
        case 'gltf':
        case 'glb':
          return this.initGltfLoader(callback);
        case 'obj':
        case 'OBJ':
          return this.initMtlLoader(callback);
        default:
          return;
      }
    }
  }
  initGltfLoader(callback) {
    this.loader = new GLTFLoader(MANAGER)
      .setCrossOrigin('anonymous')
      .setDRACOLoader(DRACO_LOADER)
      .setKTX2Loader(KTX2_LOADER.detectSupport(this.scene.renderer))
      .setMeshoptDecoder(MeshoptDecoder);
    this.loading = new Promise((resolve, reject) => {
      this.loader.load(
        this.url,
        (gltf) => {
          const object = gltf.scene;
          if (!object) {
            // Valid, but not supported by this viewer.
            throw new Error('This model contains no scene, and cannot be viewed here. However,'
              + ' it may contain individual 3D resources.');
          }
          this.clips = gltf.animations || [];
          object.visible = this.visible;

          // 部分手机不支持etc 颜色编码有问题
          this.isSupportKTX(gltf);

          // const edgeFlowLight = true;
          // if (edgeFlowLight) {
          //   this.addEdgeFlowLight(gltf);
          // }
          this.object = object;
          callback();
          resolve();
        },
        (xhr) => {
          this.emit('loading', {
            target: this,
            progress: xhr.loaded / xhr.total  * this.firstProgress,
            loaded: xhr.loaded,
            total: xhr.total,
          });
        },
        (error) => {
          console.log(error);
          reject(error);
        },
      );
    });
    return this.loading;
  }
  addEdgeFlowLight(gltf) {
    // 获取模型的第一个网格对象
    console.log('gltf', gltf.scene);
    const mesh = gltf.scene.children.find(child => child.isMesh);

    // 计算模型的边缘线
    const edgesGeometry = new EdgesGeometry(mesh.geometry);

    // 计算模型的z轴范围，z是按导出模型文件的坐标系来的，如果模型不提供box需要靠下面逻辑自己算
    // let minY = Infinity;
    // let maxY = -Infinity;
    // for (let i = 0; i < mesh.geometry.attributes.position.count; i++) {
    //   const vertex = mesh.geometry.attributes.position.array.slice(i * 3, i * 3 + 3);
    //   minY = Math.min(minY, vertex[2]);
    //   maxY = Math.max(maxY, vertex[2]);
    // }
    if (!mesh.geometry.boundingBox) {
      console.error('模型文件中没有boundingBox，没办法边缘流光');
      this.object = object;
      resolve();
      return;
    }
    // 创建自定义ShaderMaterial
    const customMaterial = new ShaderMaterial({
      uniforms: {
        rate: { value: 0.0 },
        maxZ: { value: mesh.geometry.boundingBox.max.z },
        minZ: { value: mesh.geometry.boundingBox.min.z },
        threshold: { value: 0.1 },
        color: {
          value: new Color('#FFF0BD'),
        },
        baseTexture: {
          value: mesh.material.map,
        },
      },
      vertexShader: edgeVertexShader,
      fragmentShader: edgeFragmentShader,
    });
    // 创建一个模型并设置自定义材质
    const edgeMesh = new Mesh(edgesGeometry, customMaterial);
    console.log('edgeMesh==>', edgeMesh, this.scene);
    mesh.material = customMaterial;
    this.customMaterial = customMaterial;
    this.edgeFlowLight = true;
    this.edgeFlowLightOption = {
      duration: 2000,
      customMaterial,
      time: 0,
    };
    // this.scene.scene.add(edgeMesh);
  }
  updateEdgeFlowLight() {
    const { duration } = this.edgeFlowLightOption;
    this.edgeFlowLightOption.time += 16 / duration;
    this.customMaterial.uniforms.rate.value = this.edgeFlowLightOption.time;
  }
  isSupportKTX(gltf) {
    const hasKTX = gltf.parser && gltf.parser.extensions.KHR_texture_transform;
    if (!KTX2_LOADER.workerConfig) return;
    const supportEtc = KTX2_LOADER.workerConfig.astcSupported
    || KTX2_LOADER.workerConfig.etc1Supported
    || KTX2_LOADER.workerConfig.etc2Supported
    || KTX2_LOADER.workerConfig.dxtSupported;
    if (hasKTX && !supportEtc) { // 为了解决pvrtc解压在srgb空间不正确问题
      this.scene.renderer.outputEncoding = LinearEncoding;
      this.scene.renderer.toneMapping = ReinhardToneMapping;
    } else {
      this.scene.renderer.outputEncoding = sRGBEncoding;
    }
  }
  initMtlLoader(callback) {
    this.mtlLoader = new MTLLoader(MANAGER)
      .setCrossOrigin('anonymous');
    this.loader = new OBJLoader(MANAGER)
      .setCrossOrigin('anonymous');
    if (this.targetUrl && this.materialMixUrl) {
      const mixedTexture = this.mixTexture();
      const replaceMaterial = { [this.targetUrl]: mixedTexture };
      this.mtlLoader.setMaterialOptions({
        replaceMaterial,
      });
    }

    this.loading = new Promise((resolve, reject) => {
      this.mtlLoader.load(this.mtlUrl ? this.mtlUrl : `${this.url.substring(0, this.url.lastIndexOf('.'))}.mtl`,  (materials) => {
        materials.preload();
        this.loader.setMaterials(materials).load(
          `${this.url.substring(0, this.url.lastIndexOf('.'))}.obj`,
          (obj) => {
            if (!obj) {
              // Valid, but not supported by this viewer.
              throw new Error('This model contains no scene, and cannot be viewed here. However,'
                + ' it may contain individual 3D resources.');
            }
            this.object = obj;
            callback();
            resolve();
          },
          (xhr) => {
            this.emit('loading', {
              target: this,
              progress: xhr.loaded / xhr.total  * this.firstProgress,
              loaded: xhr.loaded,
              total: xhr.total,
            });
          },
          (error) => {
            console.log(error);
            reject(error);
          },
        );
      });
    });
    return this.loading;
  }
  initLoadingManager() {
    MANAGER = new LoadingManager();
    DRACO_LOADER = new DRACOLoader(MANAGER).setDecoderPath(`${THREE_PATH}/examples/js/libs/draco/gltf/`);
    KTX2_LOADER = new KTX2Loader(MANAGER).setTranscoderPath('https://mapapi.qq.com/web/tmodel-galaxy/');
    MANAGER.onStart =  (url, itemsLoaded, itemsTotal) => {
      // 默认第一个资源占30%的进度，若是数量超过3个，及按其倒数来估算第一个资源所占进度，如数量为4，首资源进度为25%
      if (1 / itemsLoaded < this.firstProgress) {
        this.firstProgress = 1 / itemsLoaded;
      }
      this.emit('loading', {
        target: this,
        progress: 0,
        loaded: itemsLoaded,
        total: itemsTotal,
      });
    };
    MANAGER.onLoad = () => {
      this.emit('loaded', {
        target: this,
      });
    };
    MANAGER.onProgress =  (url, itemsLoaded, itemsTotal) => {
      // console.log('loading', itemsLoaded / itemsTotal, itemsTotal);
      const currentProgress = itemsLoaded / itemsTotal;
      if (currentProgress < this.firstProgress) return;
      this.emit('loading', {
        target: this,
        progress: currentProgress,
        loaded: itemsLoaded,
        total: itemsTotal,
      });
    };
    MANAGER.onError = (url) => {
      this.emit('load_failed', {
        target: this,
        error: `${url}加载错误`,
      });
    };
  }
  useFlowLight(isUse = false) {
    this.isUseFlowLight = isUse;
    if (!isUse && this.shaderMaterial) {
      this.shaderMaterial.uniforms.flowConstant.value = 100.0; // 特别大进不了筛选范围
    }
  }

  onError(error) {
    console.error('load error:', error);
  }

  addFlowLight(flowLightOptions) {
    this.flowlightRange = flowLightOptions.range || [{ x: 0, y: 1 }, { x: 0.5, y: 0.5 }];
    this.flowlightDuration = flowLightOptions.duration || 4000;
    if (this.shaderMaterial && this.shaderMaterial.uniforms) {
      this.shaderMaterial.uniforms.edgeColor.value = new Color(flowLightOptions.edgeColor || '#ff0000');
      this.shaderMaterial.uniforms.centerColor.value = new Color(flowLightOptions.centerColor || '#00ff00');
      this.shaderMaterial.uniforms.width.value = Number(flowLightOptions.width || 0.1);
      if (this.materialMixUrl !== flowLightOptions.materialMixUrl) {
        this.shaderMaterial.uniforms.texture2.value = new TextureLoader().load(flowLightOptions.materialMixUrl);
        this.materialMixUrl = flowLightOptions.materialMixUrl;
      }
    }
  }

  updateFlowlight() {
    const duration = this.flowlightDuration;
    this.animationRate = this.animationRate || 0.0;
    if (this.animationRate >= 1) {
      this.animationRate = 0.0;
    } else {
      this.animationRate += 16 / duration;
    }
    const { k, b } = this.calLineKRate(this.animationRate);
    if (this.shaderMaterial && this.shaderMaterial.uniforms) {
      this.shaderMaterial.uniforms.flowRate.value = k;
      this.shaderMaterial.uniforms.flowConstant.value = b;
    }
  }

  calLineKRate(rate) {
    if (!this.flowlightRange || this.flowlightRange.length !== 2) return { k: 0, b: 0 };
    const point1 = this.flowlightRange[0];
    const point2 = this.flowlightRange[1];
    const k = (point2.x - point1.x) / (point1.y - point2.y);
    const point3 = { x: point1.x * (1 - rate) + point2.x * rate, y: point1.y * (1 - rate) + point2.y * rate };
    const b = point3.y - k * point3.x;
    return { k, b };
  }

  mixTexture() {
    // 55-1 56-2
    const texLod = new TextureLoader();
    this.testmat = new MeshBasicMaterial({ map: texLod.load(this.targetUrl) });
    // 自定义顶点着色器对象
    this.shaderMaterial = new ShaderMaterial({
      uniforms: {
        // 纹理贴图1
        texture1: {
          value: texLod.load(this.targetUrl),
        },
        // 纹理贴图2
        texture2: {
          value: texLod.load(this.materialMixUrl),
        },
        flowRate: {
          value: 0.0,
        },
        flowConstant: {
          value: 0.0,
        },
        width: {
          value: 0.1,
        },
        centerColor: {
          value: new Color('#00ff00'),
        },
        edgeColor: {
          value: new Color('#ff0000'),
        },
      },
      // 顶点着色器
      vertexShader,
      // 片元着色器
      fragmentShader,
    });

    return this.shaderMaterial;
  }

  /**
   * 设置层叠次序
   * @param {Number} zIndex
   */
  setZIndex(zIndex = 0) {
    this.zIndex = Math.max(zIndex, 0);
  }

  /**
   * 获取层叠次序
   */
  getZIndex() {
    return this.zIndex;
  }
  getSize() {
    if (!this.object) return;
    const box = new Box3().setFromObject(this.object);
    return box.getSize(new Vector3());
  }

  /**
   * 将模型销毁
   */
  destroy() {
    this.remove();
    // 递归遍历组对象group释放所有后代网格模型绑定几何体占用内存
    if (this.object) {
      this.object.traverse((obj) => {
        if (obj.type === 'Mesh') {
          obj.geometry.dispose();
          obj.material.dispose();
        }
      });
      this.object = null;
    }
    this.removeAllListeners();
    return this;
  }

  /**
   * 显示模型
   */
  show() {
    if (this.object) {
      this.object.visible = true;
    }
    this.visible = true;
    return this;
  }

  /**
   * 隐藏模型
   */
  hide() {
    if (this.object) {
      this.object.visible = false;
    }
    this.visible = false;
    return this;
  }

  /**
   * 设置模型旋转角度
   * @param {Number[]} rotation [x, y, z]
   */
  setRotation(rotation) {
    if (isUndefined(rotation)) {
      // eslint-disable-next-line no-param-reassign
      rotation = this.rotation;
    }

    if (!isVector3(rotation)) {
      new ClassTypeError('Model.rotation', '[Number, Number, Number]', rotation).warn();
      return this;
    }

    // const euler = rotation.map((angle, index) => {
    //   // 模型坐标系为EUS，世界坐标系为ENU，在x轴上需要做一个90度的处理
    //   return !!index ? degreeToRadian(angle) : degreeToRadian(angle + 90);
    // });
    //
    // if (this.object) {
    //   this.object.rotation.set(...euler);
    //   this.object.updateMatserix();
    // }
    if (this.object) {
      this.object.rotation.set(...rotation);
    }
    this.rotation = rotation;

    return this;
  }

  getRotation() {
    return this.rotation;
  }

  /**
   * 设置模型缩放比例
   * @param {Number | Number[]} scale
   */
  setScale(scale) {
    if (isUndefined(scale)) {
      // eslint-disable-next-line no-param-reassign
      scale = this.scale;
    }

    if (isNumber(scale)) {
      this.scale = [scale, scale, scale];
    } else {
      if (isVector3(scale)) {
        this.scale = scale;
      } else {
        new ClassTypeError('Model.scale', 'Number 或 [Number, Number, Number]', scale).warn();
        return this;
      }
    }

    if (this.object) {
      this.object.scale.set(...this.scale);
      this.object.updateMatrix();
    }

    return this;
  }

  getScale() {
    return this.scale;
  }

  /**
   * 设置模型位置及锚点
   */
  setPosition(position) {
    if (!isUndefined(position)) {
      if (!isVector3(position)) {
        new ClassTypeError('Model.position', 'Vector3', position).warn();
      } else {
        this.position = position;
        if (this.object) {
          this.object.position.set(...position);
        }
      }
    }
    return this;
  }

  getPosition() {
    return this.position;
  }

  getModel() {
    return this.object;
  }

  getUrl() {
    return this.url;
  }
}
